// Session capture (spec 9.1): sampled pointer paths, pauses, focus changes
// and input device type, recorded with notice to the student. Samples go
// into the session log next to the actions.

export type Recorder = (capture: unknown, atMs: number) => void;

/** Pointer samples carry viewport coordinates and, when the bench SVG is
 * given, scene coordinates (the SVG's viewBox units) so a replay can draw
 * the pointer over the bench regardless of the student's window size. */
export function startCapture(target: HTMLElement, record: Recorder, now: () => number, svg?: SVGSVGElement): () => void {
  const SAMPLE_MS = 50;
  const scene = (x: number, y: number): { sx?: number; sy?: number } => {
    if (!svg) return {};
    const r = svg.getBoundingClientRect();
    const vb = svg.viewBox.baseVal;
    if (!r.width || !r.height || !vb.width) return {};
    return { sx: Math.round(((x - r.left) / r.width) * vb.width * 10) / 10, sy: Math.round(((y - r.top) / r.height) * vb.height * 10) / 10 };
  };
  let lastSample = -Infinity;
  let lastMove = now();
  let device: string | null = null;

  const note = (c: unknown) => record(c, now());

  const onMove = (e: PointerEvent) => {
    const t = now();
    if (device !== e.pointerType) {
      device = e.pointerType;
      note({ kind: "device", pointerType: e.pointerType });
    }
    if (t - lastMove > 2000) note({ kind: "pause", ms: Math.round(t - lastMove) });
    lastMove = t;
    if (t - lastSample >= SAMPLE_MS) {
      lastSample = t;
      note({ kind: "pointer", x: Math.round(e.clientX), y: Math.round(e.clientY), ...scene(e.clientX, e.clientY) });
    }
  };
  const onDown = (e: PointerEvent) => note({ kind: "pointerDown", x: Math.round(e.clientX), y: Math.round(e.clientY), button: e.button, ...scene(e.clientX, e.clientY) });
  const onUp = (e: PointerEvent) => note({ kind: "pointerUp", x: Math.round(e.clientX), y: Math.round(e.clientY), ...scene(e.clientX, e.clientY) });
  const onKey = (e: KeyboardEvent) => {
    if (device !== "keyboard") {
      device = "keyboard";
      note({ kind: "device", pointerType: "keyboard" });
    }
    note({ kind: "key", key: e.key.length === 1 ? "char" : e.key });
  };
  const onFocus = () => note({ kind: "focus", state: "focus" });
  const onBlur = () => note({ kind: "focus", state: "blur" });
  const onVisibility = () => note({ kind: "visibility", state: document.visibilityState });

  target.addEventListener("pointermove", onMove);
  target.addEventListener("pointerdown", onDown);
  target.addEventListener("pointerup", onUp);
  target.addEventListener("keydown", onKey);
  window.addEventListener("focus", onFocus);
  window.addEventListener("blur", onBlur);
  document.addEventListener("visibilitychange", onVisibility);
  note({ kind: "start", userAgent: navigator.userAgent, width: window.innerWidth, height: window.innerHeight, touch: navigator.maxTouchPoints > 0 });

  return () => {
    target.removeEventListener("pointermove", onMove);
    target.removeEventListener("pointerdown", onDown);
    target.removeEventListener("pointerup", onUp);
    target.removeEventListener("keydown", onKey);
    window.removeEventListener("focus", onFocus);
    window.removeEventListener("blur", onBlur);
    document.removeEventListener("visibilitychange", onVisibility);
  };
}
